<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
      <div class="max-w-4xl mx-auto">
        
        <!-- HEADER -->
        <div class="mb-8 flex items-center justify-between">
          <div>
            <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-all mb-2 group">
              <v-icon small color="currentColor" class="transition-transform group-hover:-translate-x-1">mdi-arrow-left</v-icon>
              <span class="text-[10px] font-black uppercase tracking-[0.2em]">Voltar</span>
            </button>
            <h1 class="text-2xl font-bold text-slate-700 tracking-tight">{{ id ? 'Editar Fornecedor' : 'Novo Fornecedor' }}</h1>
            <p class="text-sm text-slate-500 font-medium">Preencha as informações para {{ id ? 'atualizar' : 'cadastrar' }} o fornecedor</p>
          </div>
        </div>

        <div class="space-y-8 pb-12">
          
          <!-- SEÇÃO 1: FOTO E IDENTIFICAÇÃO -->
          <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-account-box-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">IDENTIFICAÇÃO E FOTO</h2>
            </div>
            
            <div class="p-8">
              <div class="flex flex-col md:flex-row gap-10 items-center md:items-start">
                <!-- PHOTO UPLOAD -->
                <div class="flex flex-col items-center gap-4">
                  <div 
                    class="relative group cursor-pointer"
                    @click="$refs.fileInput.click()"
                  >
                    <v-avatar size="140" class="border-4 border-slate-50 shadow-md transition-transform group-hover:scale-[1.02] bg-slate-100">
                      <img
                        :src="data.provider_logo ? $store.state.urlImages + data.provider_logo : require('@/assets/no-image.jpg')"
                        class="object-cover"
                      />
                    </v-avatar>
                    <div class="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <v-icon color="white">mdi-camera-plus-outline</v-icon>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept="image/jpeg, image/png"
                    @change="uploadImage"
                    ref="fileInput"
                    style="display: none"
                  />
                  <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Alterar Logotipo</p>
                </div>

                <!-- BASIC FIELDS -->
                <div class="flex-1 w-full space-y-6">
                  <div class="flex items-center gap-4 mb-2">
                    <button 
                      @click="data.provider_r_type = !data.provider_r_type"
                      :class="['px-5 py-2.5 rounded border-2 flex items-center gap-3 transition-all font-bold text-[10px] uppercase tracking-wider', 
                        data.provider_r_type ? 'bg-slate-800 border-slate-800 text-white shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200']"
                    >
                      <v-icon x-small :color="data.provider_r_type ? 'orange' : 'currentColor'">{{ data.provider_r_type ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                      Pessoa Jurídica (PJ)
                    </button>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-6 gap-6">
                    <div class="md:col-span-6">
                      <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">
                        {{ data.provider_r_type ? 'Razão Social' : 'Nome Completo' }}
                      </label>
                      <input 
                        type="text" 
                        v-model="data.provider_name"
                        placeholder="Nome ou Razão Social"
                        class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase font-semibold"
                      >
                    </div>
                    
                    <div class="md:col-span-3">
                      <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">
                        {{ data.provider_r_type ? 'CNPJ' : 'CPF' }}
                      </label>
                      <input 
                        type="text" 
                        v-model="data.provider_document"
                        @input="data.provider_r_type ? setCnpj(data.provider_document) : setCpf(data.provider_document)"
                        :placeholder="data.provider_r_type ? '00.000.000/0000-00' : '000.000.000-00'"
                        :class="['w-full bg-slate-50 border rounded px-3 py-2.5 text-sm transition-all focus:outline-none focus:ring-1 font-mono', 
                          (data.provider_r_type ? !validCnpj(data.provider_document) : !validCpf(data.provider_document)) && data.provider_document ? 'border-red-300 ring-red-500/10 focus:border-red-500' : 'border-slate-200 focus:border-orange-500 focus:ring-orange-500']"
                      >
                    </div>

                    <div class="md:col-span-3">
                      <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">
                        {{ data.provider_r_type ? 'Inscrição Estadual' : 'RG' }}
                      </label>
                      <input 
                        type="text" 
                        v-model="data.provider_register"
                        class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase font-semibold"
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- SEÇÃO 2: CONTATO E ENDEREÇO -->
          <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-map-marker-radius-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">CONTATO E ENDEREÇO</h2>
            </div>
            
            <div class="p-8">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div class="md:col-span-1">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">E-mail Corporativo</label>
                  <input type="email" v-model="data.provider_mail" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors lowercase italic">
                </div>
                <div>
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Celular / WhatsApp</label>
                  <input type="text" v-mask-phone.br v-model="data.provider_cel" placeholder="(00) 00000-0000" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors font-semibold">
                </div>
                <div>
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Telefone Fixo</label>
                  <input type="text" v-mask-phone.br v-model="data.provider_tel" placeholder="(00) 0000-0000" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors font-semibold">
                </div>

                <div class="md:col-span-1">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">CEP</label>
                  <div class="relative">
                    <input 
                      type="text" 
                      v-mask-cep v-model="data.provider_postal_code" 
                      @blur="getCep(data.provider_postal_code)"
                      placeholder="00.000-000"
                      class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors font-mono"
                    >
                    <v-icon small class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300">mdi-magnify</v-icon>
                  </div>
                </div>
                <div class="md:col-span-2">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Logradouro</label>
                  <input type="text" v-model="data.provider_street" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase font-semibold">
                </div>
                
                <div class="md:col-span-1">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Bairro</label>
                  <input type="text" v-model="data.provider_district" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase font-semibold">
                </div>
                <div class="md:col-span-1">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Cidade</label>
                  <input type="text" v-model="data.provider_city" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase font-semibold">
                </div>
                <div class="md:col-span-1">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">UF</label>
                  <input type="text" v-model="data.provider_state" maxlength="2" class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase font-semibold text-center">
                </div>
              </div>
            </div>
          </div>

          <!-- SEÇÃO 3: CONFIGURAÇÕES -->
          <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-wrench-cog-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">CONFIGURAÇÕES DO ATENDIMENTO</h2>
            </div>
            
            <div class="p-8">
               <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Tipo de Atendimento Especializado</label>
               <v-autocomplete
                v-model="data.attendance_id"
                :items="attendances"
                item-text="attendance_desc"
                item-value="attendance_id"
                placeholder="SELECIONE O TIPO..."
                solo flat dense hide-details
                class="custom-select"
                height="48"
              />
            </div>

            <!-- FOOTER -->
            <div class="bg-slate-50 px-8 py-5 border-t border-slate-200 flex justify-end items-center gap-4">
              <button 
                @click="$router.back()"
                class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase px-4 py-2 transition-colors flex-none"
              >
                Cancelar
              </button>
              <div class="flex-none">
                <button
                  @click="validate"
                  :disabled="loader"
                  class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] px-6 py-2.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
                >
                  <v-icon small color="white" class="mr-1" style="font-size: 14px;">mdi-check</v-icon>
                  <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white/30 border-t-white rounded-full"></span>
                  <span v-else>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- NOTIFICAÇÕES -->
        <v-snackbar
          v-model="snack.show"
          :color="snack.color"
          timeout="3000"
          rounded="lg"
          elevation="24"
        >
          <div class="flex items-center gap-3">
            <v-icon color="white" small>
              {{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            <span class="text-xs font-bold uppercase tracking-wider">{{ snack.message }}</span>
          </div>
        </v-snackbar>
      </div>
    </div>
  </v-container>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    attendances: [],
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      provider_id: null,
      attendance_id: null,
      provider_logo: "",
      provider_type: "",
      provider_name: "",
      provider_document: "",
      provider_register: "",
      provider_im: "",
      provider_mail: "",
      provider_cel: "",
      provider_tel: "",
      provider_postal_code: "",
      provider_street: "",
      provider_number: "",
      provider_complement: "",
      provider_district: "",
      provider_city: "",
      provider_state: "",
      provider_r_type: null,
    },
  }),
  created() {
    this.$http
      .get("/settings/attendance_types")
      .then((r) => (this.attendances = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`providers/${this.id}`).then((r) => {
        this.data = r.data[0];
        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    setCnpj(v) {
      v = v.replace(/\D/g, "");
      v = v.replace(/^(\d{2})(\d)/, "$1.$2");
      v = v.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
      v = v.replace(/\.(\d{3})(\d)/, ".$1/$2");
      v = v.replace(/(\d{4})(\d)/, "$1-$2");
      this.data.provider_document = v;
    },
    setCpf(v) {
      v = v.replace(/\D/g, "");
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
      this.data.provider_document = v;
    },
    validCnpj(v) {
      let cnpj = v.replace(/\D/gim, "");

      // Valida a quantidade de caracteres
      if (cnpj.length !== 14) return false;

      // Elimina inválidos com todos os caracteres iguais
      if (/^(\d)\1+$/.test(cnpj)) return false;

      // Cáculo de validação
      let t = cnpj.length - 2,
        d = cnpj.substring(t),
        d1 = parseInt(d.charAt(0)),
        d2 = parseInt(d.charAt(1)),
        calc = (x) => {
          let n = cnpj.substring(0, x),
            y = x - 7,
            s = 0,
            r = 0;

          for (let i = x; i >= 1; i--) {
            s += n.charAt(x - i) * y--;
            if (y < 2) y = 9;
          }

          r = 11 - (s % 11);
          return r > 9 ? 0 : r;
        };
      return calc(t) === d1 && calc(t + 1) === d2;
    },
    validCpf(cpf) {
      cpf = cpf.replace(/\D/g, "");
      if (cpf.toString().length != 11 || /^(\d)\1{10}$/.test(cpf)) return false;
      var result = true;
      [9, 10].forEach(function (j) {
        var soma = 0,
          r;
        cpf
          .split(/(?=)/)
          .splice(0, j)
          .forEach(function (e, i) {
            soma += parseInt(e) * (j + 2 - (i + 1));
          });
        r = soma % 11;
        r = r < 2 ? 0 : 11 - r;
        if (r != cpf.substring(j, j + 1)) result = false;
      });
      return result;
    },
    validate() {
      if (!this.data.provider_name || !this.data.provider_document) {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Atencão: Preencha o Nome e CPF/CNPJ!";
        return;
      }
      this.createOrUpdate();
    },
    createOrUpdate() {
      this.loader = true;
      if (this.id) {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .put(`/providers/${this.id}`, this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.loader = true;
            setTimeout(() => {
              this.$router.push("/providers-manager");
            }, 1500);
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .post("/providers", this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
            setTimeout(() => {
              this.$router.push("/providers-manager");
            }, 1500);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    uploadImage(event) {
      this.$store.dispatch("setLoader", true);
      const fd = new FormData();
      fd.append("image", event.target.files[0], event.target.files[0].name);
      // console.log(event.target.files[0]);
      this.$http
        .post("/upload", fd)
        .then((r) => {
          this.data.provider_logo = r.data.cover;
          this.$store.dispatch("setLoader", false);
        })
        .catch((e) => {
          alert(
            "Erro ao realizar upload da imagem tente novamente! " + e.message
          );
          this.$store.dispatch("setLoader", false);
          event.target.files = [];
        });
    },
    getCep(cep) {
      if (cep.length == 10) {
        this.loading = true;
        cep = cep.replace("-", "");
        cep = cep.replace(".", "");
        this.$http
          .get("https://viacep.com.br/ws/" + cep + "/json/")
          .then((response) => {
            this.data.provider_street = response.data.logradouro;
            this.data.provider_district = response.data.bairro;
            this.data.provider_city = response.data.localidade;
            this.data.provider_state = response.data.uf;
            this.loading = false;
          })
          .catch((e) => {
            this.message.snacshow = true;
            this.message.type = "warning";
            this.message.text = "Atencão cep não encontrado!" + e;
            this.loading = false;
          });
      }
    },
  },
};
</script>

<style scoped>
.custom-select ::v-deep .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 4px !important;
  height: 48px !important;
  padding: 0 12px !important;
}

.custom-select ::v-deep input {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #334155 !important;
  text-transform: uppercase !important;
}

.custom-select ::v-deep .v-label {
  font-size: 10px !important;
  font-weight: 900 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.15em !important;
  color: #94a3b8 !important;
}

/* Animations */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
